import React, { useState, useEffect, useLayoutEffect, useContext, useCallback } from 'react';
import { Link, navigate } from 'gatsby';
import PropTypes from 'prop-types';
import { useLocation } from '@reach/router';
import { HOW_TO, SEARCH, LEGACY, getBasePath } from 'utils/createURL';
import VersionSelector from 'components/VersionSelector/VersionSelector.component';
import { productDropdownItems, findProductItemFromBasePath, findGlobalProduct, fusion4Tags, STYLE_GUIDE_TAG, LUCIDWORKS_PLATFORM_TAG } from 'config/productItems';
import { SideBarContext, SideBarDispatchContext, initialSideBarState, UPDATE_GLOBAL_VERSION, UPDATE_GLOBAL_PRODUCT } from 'context/sidebarContextWrapper';
import FilterControls from 'components/Sidebar/Nav/FilterControls/FilterControls.component';
import learnMoreItems from 'config/learnMoreDropdownItems';
import CollapsibleList from 'components/Sidebar/CollapsibleList/CollapsibleList.component';
import ToastProductChange from 'components/Toast/ToastProductChange.component';
import Hamburger from 'images/icons/hamburger.inline.svg'
import HamClose from 'images/icons/ham-close.inline.svg'
import styled from 'styled-components';
import useWindowSize from 'hooks/useWindowSize';
import HowToNav from './Nav/HowTo/HowTo';
import ProductNav from './Nav/ProductNav/ProductNav.component'; 
import { NOT_FOUND } from '../../utils/constants';
import PoliciesNav from './Nav/StaticNav/PoliciesNav';
import SolutionsNav from './Nav/StaticNav/SolutionsNav';
import {sidebar, mainLogo, mobileIndicator, hamburger, MainFacetLabel, homeLink, legacy} from './styles.module.scss';


const Header = styled.header`
  height: ${props => props.$menuopen ? '100%' : '5em'};

  @media screen and (min-width: 767px){
    height: 100vh;
  }
`

const Navigation = styled.nav`

  // overflow-x: visible on desktop view to prevent sidebar cutoff DOCS-3262

  display: flex;
  flex-direction: column;
  margin-top: ${props => props.$search ? '4.5em' : '0'};
  margin-bottom: 4em;
  padding-bottom: 2em;
  scrollbar-width: thin;
  transition: transform 100ms cubic-bezier(0,0,0,0.99);
  overflow-x: hidden;
  overflow-y: ${props => props.$menuopen ? 'unset' : 'auto'};
  transform: ${props => props.$menuopen ? 'translateX(0)' : 'translateX(-110%)'};
  height: ${props => props.$menuopen ? 'calc(80% - 6em)' : '0'};

  @media screen and (min-width: 767px){
    margin-top: 0;
    margin-bottom: 0;
    transform: translateX(0);
    overflow-y: auto;
    height: auto;
  }
`;

function Sidebar({ 
  path, 
  sourcePath, 
  searchParams = '', 
  isSearch,
  pageData = {}
}){
  const state = useContext(SideBarContext) || initialSideBarState;
  const dispatch = useContext(SideBarDispatchContext);
  const [isClient, setIsClient] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const {width} = useWindowSize();
  const location = useLocation();

  let version = state.currentVersion;

  const basePath = getBasePath(path);

  function sendProductDispatch(newProduct){
    if(newProduct && state.currentProduct?.value !== newProduct?.value){
      dispatch({
        type: UPDATE_GLOBAL_PRODUCT,
        payload: newProduct
      })
      dispatch({
        type: UPDATE_GLOBAL_VERSION,
        payload: newProduct.label.replace(' ', '-')
      })
    }
  }

  useEffect(() => {
   // document.getElementsByTagName('html')[0].style.overflow = 'auto'
    if(width >= 767){
      document.getElementsByTagName('body')[0].style.overflow = '';
      document.getElementsByTagName('html')[0].style.overflow = ''
      setMenuOpen(false)
    }
  }, [width])


  useEffect(() => {
    document.getElementsByTagName('body')[0].style.overflow = '';
    document.getElementsByTagName('html')[0].style.overflow = ''
  }, [location])

  useEffect(() => {
    if (state.currentVersion && version !== state.currentVersion && basePath !== 'policies' && basePath !== 'solutions') {
      dispatch({
        type: UPDATE_GLOBAL_VERSION,
        payload: version,
      });
    }
  }, [version, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps


  useLayoutEffect(() => {
    let updateProduct = state.currentProduct
    const isValidProductPage = basePath !== HOW_TO && basePath !== SEARCH && basePath !== 'policies' && basePath !== 'solutions' && basePath !== '*';

    if(searchParams === NOT_FOUND){
      updateProduct = findGlobalProduct(basePath, version, productDropdownItems)
      if(updateProduct){
        sendProductDispatch(updateProduct)
      }
      return;
    }

    if(isValidProductPage && searchParams !== NOT_FOUND){
      updateProduct = findGlobalProduct(basePath, version, productDropdownItems)
      sendProductDispatch(updateProduct)
      return
    }

    if((basePath === HOW_TO && state.currentProduct.tag === LEGACY) || (basePath === HOW_TO && state.currentProduct.tag === STYLE_GUIDE_TAG ) ){
      updateProduct = initialSideBarState.currentProduct
      dispatch({
        type: UPDATE_GLOBAL_VERSION,
        payload: initialSideBarState.currentVersion
      })
      sendProductDispatch(updateProduct)
      return
    }

    if(path === '/'){
      updateProduct = findGlobalProduct(state.currentProduct.tag, version, productDropdownItems)
      sendProductDispatch(updateProduct)
    }
  }, [path, state.currentProduct, dispatch])  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsClient(true);
  }, [setIsClient]);


  const handleGlobalVersionChange = useCallback((newProduct) => {
    dispatch({
      type: UPDATE_GLOBAL_VERSION, 
      payload: newProduct.label.replace(' ', '-'),
    });
    dispatch({
      type: UPDATE_GLOBAL_PRODUCT,
      payload: newProduct
    })
    
    if(newProduct.tag === LEGACY || newProduct.tag === STYLE_GUIDE_TAG){
      navigate(newProduct.link)
      return;
    }

    if(newProduct.tag === LUCIDWORKS_PLATFORM_TAG && basePath !== HOW_TO ){
      navigate(newProduct.link)
      return;
    }
    
    if(path === "/" || basePath !== HOW_TO && basePath !== newProduct.tag && (!fusion4Tags.includes(basePath) || !fusion4Tags.includes(newProduct.tag))){
      navigate(newProduct.link);
    } else if (path !== '/' && path !== '/how-to'){
      const newURL = path.replace(`/${version}/`, `/${newProduct.label.replace(' ', '-')}/`);
      navigate(newURL)
    }
  }, [basePath, dispatch, path, version]);

  let currentProductObj = findProductItemFromBasePath(state.currentProduct.tag);
  const isProductPageSSR = basePath !== HOW_TO && basePath !== SEARCH && basePath !== '*' && searchParams !== NOT_FOUND; // Path is '/*' for the root page at compile time
  if (isProductPageSSR) {
    const productCheck = findProductItemFromBasePath(basePath);
    if (productCheck) currentProductObj = productCheck;
    if (currentProductObj && (currentProductObj.versions || currentProductObj.apptypes )) {
      version = path.split('/')[2];
    } 
  }
  
  if(searchParams === NOT_FOUND){
    const productCheck = findGlobalProduct(basePath, version, productDropdownItems)
    if(productCheck){
      version = path.split('/')[2];
    }
  }

  const versionSelector = ( 
    <VersionSelector
      handleChange={handleGlobalVersionChange}
      selectedOption={state.currentProduct}
      options={productDropdownItems}
      display="selectorLight"
    />
  );

  let navContent =<ProductNav 
                    basePath={state.currentProduct.tag} 
                    productInfo={currentProductObj} 
                    recentVersion={version} 
                    currentLink={state.currentProduct.link} 
                    currentProduct={state.currentProduct.value}
                  />

  if (isProductPageSSR && path !== '/' && (searchParams !== NOT_FOUND || currentProductObj !== undefined) && basePath !== 'policies' && basePath !== 'solutions' ) {
    navContent = <ProductNav 
                      basePath={basePath} 
                      productInfo={currentProductObj} 
                      recentVersion={version} 
                      currentLink={state.currentProduct.link} 
                      currentProduct={state.currentProduct.value}
                    />
  } else if ((basePath === HOW_TO && path !== '/' && path !== '/how-to')){

    const pageSplitPath = sourcePath.split('/');
    const isGuide = pageSplitPath.length >= 3;
    const guideName = pageSplitPath[1];

    navContent = <HowToNav 
                    path={path} 
                    currentLink={state.currentProduct.link} 
                    currentProduct={state.currentProduct.value} 
                    currentVersion={state.currentVersion}
                    currentTag={state.currentProduct.tag}
                    isGuide={isGuide}
                    guideName={guideName}
                    pageData={pageData}
                  />
  }

  const handleMenuClick = useCallback(() => {
    setMenuOpen(prevState => !prevState)
    if(menuOpen){
      document.getElementsByTagName('body')[0].style.overflow = '';
      document.getElementsByTagName('html')[0].style.overflow = ''
    } else {
      document.getElementsByTagName('html')[0].style.overflow = 'hidden'
      document.getElementsByTagName('body')[0].style.overflow = 'hidden'
    }
  }, [menuOpen])

  return (
    <Header className={sidebar} $menuopen={menuOpen}>
      <div className={`${mainLogo} main-logo`}>
        <Link to="/" style={{lineHeight: '1'}} aria-label="Lucidworks Logo">
          <img alt="Lucidworks Logo"  className="lw-logo" src="data:," width="130px" height="19px"/>
        </Link>
        <div className={mobileIndicator}>
          <p style={{textTransform: 'capitalize'}}>{state.currentProduct.value}</p>
          <button className={hamburger} type="button" aria-label="toggle mobile navigation" onClick={handleMenuClick}>
            {menuOpen ? <HamClose className="hamburger-icon"/> : <Hamburger className="hamburger-icon" />}
          </button>
        </div>
      </div>
      
      <div className={`mainSelector-${menuOpen}`}>
        {!isSearch && (
          <>
            <div className={MainFacetLabel}>
              <p>Product Selector</p>
            </div>
            {searchParams === NOT_FOUND ? (isClient && versionSelector) : versionSelector}        
          </>
        )}
      </div>

      <Navigation $menuopen={menuOpen} $search={isSearch}>
        {isSearch ?
          <>
            <Link to='/' className={`${isSearch ? homeLink : undefined}`}>Home</Link>
            <Link to="/how-to" >How-to Guides</Link>
            <FilterControls searchParams={searchParams}/>
          </>
          :
          <>
            {navContent}
            <PoliciesNav />
            <SolutionsNav />
            <CollapsibleList items={learnMoreItems} className='learn-more-dropdown'/>
          </>
        }
        <a href="https://legacydoc.lucidworks.com" target="blank" className={legacy}>Legacy Docs</a>
      </Navigation>

      <ToastProductChange 
        currentProduct={state.currentProduct.value}
      />

    </Header>
  );
};


Sidebar.propTypes = {
  path: PropTypes.string.isRequired,
  sourcePath: PropTypes.string.isRequired,
  searchParams: PropTypes.string,
  isSearch: PropTypes.bool.isRequired,
  pageData: PropTypes.object
};

export default Sidebar;
